import type { ClientGame, ClientGameCtx } from "./index";
import { WRM, WRM_SIZES } from "../../shared/arcade";
import { sound } from "../sound";
import { C, SEAT_COLORS, displayFont, bodyFont, hudPill } from "../theme";
import { roundRectPath } from "../draw";

const COL_W = WRM.W / WRM.COLS;
// a bright day over the island — the light sticker palette (theme.ts),
// matching the native app's WormsGame.tsx. Worms take their seat colours.
const COLORS: string[] = [SEAT_COLORS[0], SEAT_COLORS[1]];
const SKY_TOP = "#7CC8FF";
const SKY_BOTTOM = "#D6F1FF";
const SEA = "#4D8DFF";
const DIRT = "#E3A564";
const STONE = "#F6E6C4";
const MAX_DRAG = 150;
/** CSS px kept clear above the world: health pills + the wind/turn chips */
const HUD_TOP = 84;
/** CSS px kept clear below the world: the bomb sizes + FIRE pad */
const BOTTOM_UI = 104;
/** max side margin, CSS px, when the world is width-bound */
const SIDE = 6;
/** wind/turn chip text size */
const CHIP = 14;

const short = (s: string) => (s.length > 9 ? `${s.slice(0, 8)}…` : s);

/** env(safe-area-inset-*) in CSS px — the page uses viewport-fit=cover */
function readSafeInsets(): { top: number; bottom: number } {
  try {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;" +
      "padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)";
    document.body.appendChild(probe);
    const cs = getComputedStyle(probe);
    const r = { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 };
    probe.remove();
    return r;
  } catch {
    return { top: 0, bottom: 0 };
  }
}

/** the width theme.ts hudPill() will draw for this text — for centring a row */
function pillWidth(g: CanvasRenderingContext2D, text: string, size: number, label?: string): number {
  g.save();
  let labelW = 0;
  if (label) {
    g.font = bodyFont(Math.round(size * 0.62), 900);
    labelW = g.measureText(label).width + size * 0.4;
  }
  g.font = displayFont(size);
  const w = Math.round(g.measureText(text).width + labelW + size * 1.3);
  g.restore();
  return w;
}

/** a white HUD sticker pill: seat-colour disc, player name, health number */
function seatPill(
  g: CanvasRenderingContext2D,
  x: number, y: number,
  name: string, hp: number, colour: string,
  align: "left" | "right",
) {
  const h = 32;
  g.save();
  g.font = bodyFont(12, 900);
  const nw = g.measureText(name).width;
  g.font = displayFont(17);
  const hw = g.measureText(String(hp)).width;
  const w = Math.round(10 + 14 + 6 + nw + 8 + hw + 12);
  const left = align === "right" ? x - w : x;
  g.fillStyle = C.ink;
  roundRectPath(g, left, y + 3, w, h, h / 2);
  g.fill();
  roundRectPath(g, left, y, w, h, h / 2);
  g.fillStyle = C.white;
  g.fill();
  g.strokeStyle = C.ink;
  g.lineWidth = 2.5;
  g.stroke();
  g.beginPath();
  g.arc(left + 17, y + h / 2, 7, 0, Math.PI * 2);
  g.fillStyle = colour;
  g.fill();
  g.lineWidth = 2;
  g.stroke();
  g.textAlign = "left";
  g.textBaseline = "middle";
  g.font = bodyFont(12, 900);
  g.fillStyle = C.muted;
  g.fillText(name, left + 30, y + h / 2 + 1);
  g.font = displayFont(17);
  g.fillStyle = C.ink;
  g.fillText(String(hp), left + 30 + nw + 8, y + h / 2 + 1.5);
  g.restore();
}

interface Deb { x: number; y: number; vx: number; vy: number; ttl: number; c: string; }

export class WormsClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private pad!: HTMLDivElement;
  private raf = 0;
  private last = performance.now();
  private t = 0;
  private deb: Deb[] = [];

  // aim drag (screen space)
  private dragging = false;
  private dragCur = { x: 0, y: 0 };
  private aim = -0.85;
  private power = 0.62;
  private size = 1;
  private lastSet = 0;
  private view = { ox: 0, oy: 0, scale: 1 };
  private safe = { top: 0, bottom: 0 };

  // "never expires" (persistent) matches: web-to-web only, no automatic push —
  // once it's genuinely the opponent's turn, offer a one-tap WhatsApp ping so
  // they find out without either of us needing to leave the app open. See
  // ArenaRoom's persistent-room notes and the "Game never expires" lobby toggle.
  private notifyBar: HTMLDivElement | null = null;
  // the turnSlot value already prompted for, so re-render / re-sync calls
  // while it's still their turn don't reshow a dismissed banner — reset to
  // -1 whenever it's my turn again, arming it fresh for the next handoff
  private notifiedTurnSlot = -1;

  private get st() { return this.ctx.room.state.worms; }
  private get mine() { return this.ctx.mySlot; }
  private get myTurn() { return this.st.turnSlot === this.mine && this.st.phase === "aim"; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);
    this.buildPad(ctx.root);
    this.safe = readSafeInsets();

    const w = this.st.worms?.[this.mine];
    if (w) { this.aim = w.aim; this.power = w.power; this.size = w.size; }

    ctx.room.onMessage("w-fire", () => sound.laser());
    ctx.room.onMessage("w-boom", (m: any) => {
      sound.rockBoom((m?.size ?? 1) + 1);
      this.explode(m?.x ?? 0, m?.y ?? 0, m?.size ?? 1);
      try { navigator.vibrate?.(20 + (m?.size ?? 0) * 20); } catch { /* */ }
    });
    ctx.room.onMessage("w-thud", () => sound.place());

    const hint = document.createElement("div");
    hint.className = "touch-hint";
    hint.textContent = "drag to aim · press FIRE to launch";
    ctx.root.appendChild(hint);
    setTimeout(() => hint.remove(), 4200);

    this.canvas.addEventListener("pointerdown", this.onDown);
    window.addEventListener("pointermove", this.onMove);
    window.addEventListener("pointerup", this.onUp);
    window.addEventListener("keydown", this.onKey);
    this.loop();
    // main.ts's sync() calls are driven by state *changes* — an idle
    // "never expires" match resumed straight into "waiting on them" has
    // nothing left to change until someone acts, so set the initial notify
    // banner state directly here rather than waiting on a patch that may
    // never come.
    this.sync();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointerdown", this.onDown);
    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("keydown", this.onKey);
    this.pad?.remove();
    this.notifyBar?.remove();
  }

  sync() {
    const persistent = !!this.ctx.room.state.persistent;
    if (!persistent) { this.hideNotifyBar(); this.notifiedTurnSlot = -1; return; }
    const st = this.st;
    if (st.turnSlot === this.mine) {
      // it's my turn (or back to being my turn) — clear any banner and arm
      // for the *next* handoff
      this.hideNotifyBar();
      this.notifiedTurnSlot = -1;
      return;
    }
    // waiting on the opponent — show once their turn has actually started
    // (phase "aim", not mid-flight/settle from the shot that just landed)
    if (st.phase === "aim" && st.turnSlot !== this.notifiedTurnSlot) {
      this.showNotifyBar();
      this.notifiedTurnSlot = st.turnSlot;
    }
  }

  private showNotifyBar() {
    if (this.notifyBar) return;
    const bar = document.createElement("div");
    bar.className = "wrm-notify";
    const text = document.createElement("span");
    text.className = "wrm-notify-text";
    text.textContent = "Move's in —";
    const link = document.createElement("a");
    link.className = "wrm-notify-link";
    link.textContent = "🔔 Notify on WhatsApp";
    link.target = "_blank";
    link.rel = "noopener";
    const code = String(this.ctx.room.state.code || "");
    const url = `${location.origin}/?code=${code}`;
    link.href = `https://wa.me/?text=${encodeURIComponent(`Your move in Artillery Worms! ${url}`)}`;
    const dismiss = document.createElement("button");
    dismiss.className = "wrm-notify-x";
    dismiss.textContent = "✕";
    dismiss.setAttribute("aria-label", "Dismiss");
    dismiss.onclick = () => this.hideNotifyBar();
    bar.append(text, link, dismiss);
    this.ctx.root.appendChild(bar);
    this.notifyBar = bar;
  }

  private hideNotifyBar() {
    this.notifyBar?.remove();
    this.notifyBar = null;
  }

  // ---- controls ----------------------------------------------------------

  private buildPad(root: HTMLElement) {
    this.pad = document.createElement("div");
    this.pad.className = "wrm-pad";
    const sizes = document.createElement("div");
    sizes.className = "wrm-sizes";
    WRM_SIZES.forEach((lbl, i) => {
      const b = document.createElement("button");
      b.className = "wrm-size";
      b.dataset.i = String(i);
      b.textContent = lbl;
      b.onclick = () => { this.size = i; this.pushSet(true); this.syncPad(); };
      sizes.appendChild(b);
    });
    const fire = document.createElement("button");
    fire.className = "wrm-fire";
    fire.textContent = "FIRE";
    fire.onclick = () => this.fire();
    this.pad.append(sizes, fire);
    root.appendChild(this.pad);
    this.syncPad();
  }

  private syncPad() {
    if (!this.pad) return;
    this.pad.classList.toggle("live", this.myTurn);
    this.pad.querySelectorAll<HTMLElement>(".wrm-size").forEach((b) => {
      b.classList.toggle("on", Number(b.dataset.i) === this.size);
    });
  }

  private toWorld(px: number, py: number) {
    const r = this.canvas.getBoundingClientRect();
    return {
      x: (px - r.left - this.view.ox) / this.view.scale,
      y: (py - r.top - this.view.oy) / this.view.scale,
    };
  }

  private onDown = (e: PointerEvent) => {
    if (!this.myTurn) return;
    this.dragging = true;
    this.dragCur = this.toWorld(e.clientX, e.clientY);
    this.updateAimFromDrag();
    e.preventDefault();
  };
  private onMove = (e: PointerEvent) => {
    if (!this.dragging) return;
    this.dragCur = this.toWorld(e.clientX, e.clientY);
    this.updateAimFromDrag();
  };
  private onUp = () => {
    // drag only aims — you fire deliberately with the FIRE button (or space)
    this.dragging = false;
  };
  private onKey = (e: KeyboardEvent) => {
    if (!this.myTurn) return;
    let used = true;
    if (e.code === "ArrowLeft") this.aim -= 0.03;
    else if (e.code === "ArrowRight") this.aim += 0.03;
    else if (e.code === "ArrowUp") this.power = Math.min(1, this.power + 0.04);
    else if (e.code === "ArrowDown") this.power = Math.max(0.05, this.power - 0.04);
    else if (e.code === "Digit1") this.size = 0;
    else if (e.code === "Digit2") this.size = 1;
    else if (e.code === "Digit3") this.size = 2;
    else if (e.code === "Space") { this.fire(); }
    else used = false;
    if (used) { e.preventDefault(); this.pushSet(); this.syncPad(); }
  };

  private updateAimFromDrag() {
    const w = this.st.worms?.[this.mine];
    if (!w) return;
    const dx = this.dragCur.x - w.x;
    const dy = this.dragCur.y - w.y;
    let a = Math.atan2(dy, dx);
    if (Math.sin(a) > 0.05) a = dx >= 0 ? -0.05 : -Math.PI + 0.05; // clamp to upward
    this.aim = a;
    const len = Math.hypot(dx, dy) * this.view.scale;
    this.power = Math.max(0.06, Math.min(1, len / MAX_DRAG));
    this.pushSet();
  }

  private pushSet(force = false) {
    const now = performance.now();
    if (!force && now - this.lastSet < 90) return;
    this.lastSet = now;
    if (this.myTurn) this.ctx.room.send("set", { aim: this.aim, power: this.power, size: this.size });
  }

  private fire() {
    if (!this.myTurn) return;
    this.ctx.room.send("fire", { aim: this.aim, power: this.power, size: this.size });
    this.dragging = false;
  }

  // ---- fx ---------------------------------------------------------------

  private explode(x: number, y: number, size: number) {
    const n = 16 + size * 12;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = 0.03 + Math.random() * 0.12 * (1 + size * 0.3);
      this.deb.push({
        x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 0.05,
        ttl: 400 + Math.random() * 500,
        c: Math.random() < 0.5 ? "#B7713A" : (Math.random() < 0.5 ? C.mango : C.coral),
      });
    }
  }

  // ---- render ----------------------------------------------------------

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this.t += dt;
    this.syncPad();
    try { this.draw(dt); } catch (e) { console.error("worms frame", e); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private heights(): number[] {
    return String(this.st.heights || "").split(",").map(Number);
  }

  private simArc(): { x: number; y: number }[] {
    const w = this.st.worms?.[this.mine];
    if (!w) return [];
    const sp = WRM.BASE_V * (WRM.SIZE_V[this.size] ?? 1) * (0.15 + 0.85 * this.power);
    const hs = this.heights();
    let x = w.x + Math.cos(this.aim) * WRM.MUZZLE;
    let y = w.y + Math.sin(this.aim) * WRM.MUZZLE - 3;
    let vx = Math.cos(this.aim) * sp, vy = Math.sin(this.aim) * sp;
    const pts: { x: number; y: number }[] = [];
    const h = 14;
    for (let i = 0; i < 160; i++) {
      vx += this.st.wind * h;
      vy += WRM.GRAV * h;
      x += vx * h;
      y += vy * h;
      if (x < -20 || x > WRM.W + 20 || y > WRM.H) break;
      const col = Math.max(0, Math.min(WRM.COLS - 1, Math.floor(x / COL_W)));
      if (y >= WRM.H - (hs[col] ?? 0)) break;
      if (i % 2 === 0) pts.push({ x, y });
    }
    return pts;
  }

  private draw(dt: number) {
    const st = this.st;
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) {
      c.width = cw * dpr; c.height = ch * dpr;
      this.safe = readSafeInsets();
    }
    g.save();
    g.scale(dpr, dpr);
    g.lineJoin = "round";
    g.lineCap = "round";

    // The world is landscape, so it runs the full screen width and sits on the
    // bottom controls; the sky carries on above it (so high lobs stay visible)
    // and the land/sea carry on below and past the sides — no empty bars. The
    // pointer handlers invert this same ox/oy/scale (via this.view).
    const top = this.safe.top + HUD_TOP;
    const bottom = this.safe.bottom + BOTTOM_UI;
    const scale = Math.max(0.1, Math.min((cw - SIDE * 2) / WRM.W, (ch - top - bottom) / WRM.H));
    const ox = (cw - WRM.W * scale) / 2;
    const oy = ch - bottom - WRM.H * scale;
    this.view = { ox, oy, scale };
    const padX = ox / scale + 2;                      // world units past each side edge
    const padT = oy / scale + 2;
    const padB = (ch - oy) / scale - WRM.H + 2;
    const fullW = WRM.W + padX * 2;
    const px1 = 1 / scale;                            // one CSS px, in world units

    g.save();
    g.translate(ox, oy);
    g.scale(scale, scale);

    // sky, from the top of the screen down to the waterline — the arc flies
    // through it above the strip
    const sky = g.createLinearGradient(0, -padT, 0, WRM.WATER);
    sky.addColorStop(0, SKY_TOP);
    sky.addColorStop(1, SKY_BOTTOM);
    g.fillStyle = sky;
    g.fillRect(-padX, -padT, fullW, WRM.WATER + padT);

    // water — runs on below the world to the bottom of the screen, so the
    // island sits in the sea under the controls
    g.fillStyle = SEA;
    g.fillRect(-padX, WRM.WATER, fullW, WRM.H - WRM.WATER + padB);
    g.strokeStyle = "rgba(255,255,255,0.45)";
    g.lineWidth = 2 * px1;
    for (let i = 0; i < 3; i++) {
      const y = WRM.WATER + 5 + i * 7 + Math.sin(this.t / 700 + i) * 2;
      g.beginPath(); g.moveTo(-padX, y); g.lineTo(WRM.W + padX, y); g.stroke();
    }

    // terrain: dirt, carried flat past the side edges and down to the bottom
    // of the screen, then an inked grass edge along the surface
    const hs = this.heights();
    if (hs.length) {
      const surface = () => {
        g.moveTo(-padX, WRM.H - (hs[0] ?? 0));
        for (let cI = 0; cI < WRM.COLS; cI++) {
          g.lineTo(cI * COL_W + COL_W / 2, WRM.H - (hs[cI] ?? 0));
        }
        g.lineTo(WRM.W + padX, WRM.H - (hs[WRM.COLS - 1] ?? 0));
      };
      g.beginPath();
      surface();
      g.lineTo(WRM.W + padX, WRM.H + padB);
      g.lineTo(-padX, WRM.H + padB);
      g.closePath();
      g.fillStyle = DIRT;
      g.fill();
      g.beginPath();
      surface();
      g.strokeStyle = C.ink;
      g.lineWidth = 7 * px1;
      g.stroke();
      g.strokeStyle = C.green;
      g.lineWidth = 4 * px1;
      g.stroke();
    }
    // waterline
    g.strokeStyle = "rgba(255,255,255,0.7)";
    g.lineWidth = 2 * px1;
    g.beginPath(); g.moveTo(-padX, WRM.WATER); g.lineTo(WRM.W + padX, WRM.WATER); g.stroke();

    // forts
    st.forts?.forEach((fb: any) => {
      const dmg = 1 - Math.max(0, Math.min(1, fb.hp / WRM.BLOCK_HP));
      g.fillStyle = STONE;
      g.fillRect(fb.x, fb.y, fb.w, fb.h);
      g.strokeStyle = C.ink;
      g.lineWidth = 1.5 * px1;
      g.strokeRect(fb.x, fb.y, fb.w, fb.h);
      if (dmg > 0.1) {
        g.strokeStyle = `rgba(27,21,52,${0.4 + dmg * 0.5})`;
        g.lineWidth = 1.2 * px1;
        g.beginPath();
        g.moveTo(fb.x + fb.w * 0.3, fb.y);
        g.lineTo(fb.x + fb.w * 0.5, fb.y + fb.h * (0.4 + dmg * 0.4));
        g.lineTo(fb.x + fb.w * 0.4, fb.y + fb.h);
        g.stroke();
      }
    });

    // last crater scorch
    if (st.lastX >= 0) {
      g.fillStyle = "rgba(27,21,52,0.18)";
      g.beginPath();
      g.arc(st.lastX, st.lastY, (WRM.SIZE_R[st.lastSize] ?? 20) * 0.8, 0, Math.PI * 2);
      g.fill();
    }

    // predicted arc (my turn, aiming) — ink dots, fading with distance
    if (this.myTurn) {
      const pts = this.simArc();
      g.fillStyle = C.ink;
      pts.forEach((p, i) => {
        g.globalAlpha = Math.max(0.15, 0.75 * (1 - i / pts.length));
        g.beginPath(); g.arc(p.x, p.y, 2 * px1, 0, Math.PI * 2); g.fill();
      });
      g.globalAlpha = 1;
    }

    // worms
    st.worms?.forEach((w: any, slot: number) => {
      const dead = w.hp <= 0;
      const col = COLORS[slot] ?? C.mango;
      g.save();
      g.translate(w.x, w.y);
      // gun barrel for the active aiming worm — ink under a seat-coloured core
      const showAim = st.phase === "aim" && slot === st.turnSlot && !dead;
      const a = slot === this.mine ? this.aim : w.aim;
      if (showAim) {
        const pw = slot === this.mine ? this.power : w.power;
        const tx = Math.cos(a) * (10 + pw * 14), ty = Math.sin(a) * (10 + pw * 14) - 2;
        g.beginPath(); g.moveTo(0, -2); g.lineTo(tx, ty);
        g.strokeStyle = C.ink;
        g.lineWidth = 5 * px1;
        g.stroke();
        g.strokeStyle = col;
        g.lineWidth = 2.5 * px1;
        g.stroke();
      }
      g.beginPath();
      g.ellipse(0, 0, 6.5, 7, 0, 0, Math.PI * 2);
      g.fillStyle = dead ? C.faint : col;
      g.fill();
      g.strokeStyle = C.ink;
      g.lineWidth = 2 * px1;
      g.stroke();
      if (!dead) {
        // eye looks the way the barrel points
        const look = showAim ? (Math.cos(a) >= 0 ? 1 : -1) : (slot === 0 ? 1 : -1);
        g.fillStyle = C.white;
        g.beginPath(); g.arc(look * 2.4, -1.8, 2.2, 0, Math.PI * 2); g.fill();
        g.fillStyle = C.ink;
        g.beginPath(); g.arc(look * 3, -1.8, 1, 0, Math.PI * 2); g.fill();
        // hard hat
        g.beginPath();
        g.arc(0, -4, 5.5, Math.PI, 0);
        g.lineTo(6, -4); g.lineTo(-6, -4);
        g.closePath();
        g.fillStyle = C.mango;
        g.fill();
        g.lineWidth = 1.4 * px1;
        g.stroke();
      }
      g.restore();
    });

    // in-flight projectile — an ink ball with a white trail so it reads
    // against both the bright sky and the dirt
    if (st.projActive) {
      g.strokeStyle = "rgba(255,255,255,0.85)";
      g.lineWidth = 3 * px1;
      g.beginPath(); g.moveTo(st.projX, st.projY); g.lineTo(st.projX - 6, st.projY - 6); g.stroke();
      g.fillStyle = C.ink;
      g.beginPath(); g.arc(st.projX, st.projY, 3.2, 0, Math.PI * 2); g.fill();
    }

    // debris
    for (let i = this.deb.length - 1; i >= 0; i--) {
      const d = this.deb[i]!;
      d.ttl -= dt; if (d.ttl <= 0) { this.deb.splice(i, 1); continue; }
      d.vy += 0.00025 * dt;
      d.x += d.vx * dt; d.y += d.vy * dt;
      g.globalAlpha = Math.min(1, d.ttl / 300);
      g.fillStyle = d.c;
      g.fillRect(d.x - 1, d.y - 1, 2.5, 2.5);
      g.globalAlpha = 1;
    }

    g.restore();

    // ---- HUD (screen space) ----
    // hp over each worm, in screen px so it stays legible at phone scale
    g.textAlign = "center";
    g.textBaseline = "alphabetic";
    g.font = bodyFont(12, 900);
    st.worms?.forEach((w: any) => {
      const dead = w.hp <= 0;
      const sx = ox + w.x * scale, sy = oy + (w.y - 12) * scale - 4;
      const txt = dead ? "✗" : String(Math.ceil(w.hp));
      g.strokeStyle = C.white;
      g.lineWidth = 3.5;
      g.strokeText(txt, sx, sy);
      g.fillStyle = dead ? C.faint : C.ink;
      g.fillText(txt, sx, sy);
    });

    // a health pill per side: slot 0 just right of the quit button, slot 1
    // at the top-right
    const hudY = this.safe.top + 8;
    st.worms?.forEach((w: any, slot: number) => {
      const p = this.ctx.room.state.players.get
        ? [...(this.ctx.room.state.players as any).values()].find((pl: any) => pl.slot === slot)
        : null;
      const name = slot === this.mine ? "You" : short(p?.name || `P${slot + 1}`);
      seatPill(g, slot === 0 ? 56 : cw - 12, hudY, name, Math.max(0, Math.ceil(w.hp ?? 0)),
        COLORS[slot] ?? C.mango, slot === 0 ? "left" : "right");
    });

    // wind + turn chips, centred under the health pills
    const windPx = st.wind / WRM.WIND_MAX;
    const arrow = windPx > 0.05 ? "→".repeat(Math.min(4, Math.ceil(Math.abs(windPx) * 4)))
      : windPx < -0.05 ? "←".repeat(Math.min(4, Math.ceil(Math.abs(windPx) * 4))) : "calm";
    // a "never expires" match ignores the per-turn clock server-side (see
    // games/worms.ts tick()) — st.turnLeft just sits at its starting value,
    // so don't render a countdown that isn't actually counting down
    const persistent = !!this.ctx.room.state.persistent;
    const secs = Math.ceil((st.turnLeft ?? 0) / 1000);
    let turnText: string;
    let turnFill: string = C.white;
    if (st.phase === "aim") {
      turnText = persistent
        ? (this.myTurn ? "Your shot · no rush" : "Opponent aiming · no rush")
        : (this.myTurn ? `Your shot · ${secs}s` : `Opponent aiming · ${secs}s`);
      if (st.turnSlot >= 0) turnFill = COLORS[st.turnSlot] ?? C.white;
    } else {
      turnText = st.phase === "fly" ? "Incoming!" : "…";
    }
    const chipY = hudY + 40;
    const windW = pillWidth(g, arrow, CHIP, "Wind");
    const turnW = pillWidth(g, turnText, CHIP);
    const x0 = (cw - (windW + 8 + turnW)) / 2;
    hudPill(g, x0, chipY, arrow, { size: CHIP, label: "Wind" });
    hudPill(g, x0 + windW + 8, chipY, turnText, { size: CHIP, fill: turnFill });
    g.restore();
  }

  results(ctx: ClientGameCtx): HTMLElement {
    const st = ctx.room.state.worms;
    const a = st.worms?.[0], b = st.worms?.[1];
    const d = document.createElement("div");
    d.className = "arc-final";
    d.innerHTML =
      `<div class="score-big">${Math.max(0, Math.ceil(a?.hp ?? 0))} — ${Math.max(0, Math.ceil(b?.hp ?? 0))}</div>` +
      `<div class="arc-note">${st.endReason === "time" ? "time up — most health wins" : "health remaining"}</div>`;
    return d;
  }
}
